/**
 * Camera and pinned positions of the Workbench canvas, per tab session.
 *
 * Only the camera (zoom and pan) and the nodes the officer dragged
 * (`manually-positioned`) are stored; everything else re-lays out on return.
 * The record is keyed by the canvas version (hash of the address-node ids, the
 * same one the Links memo uses): a view saved for a different set of wallets is
 * ignored rather than applied to the wrong picture.
 *
 * Storage: sessionStorage `workbench-view`, one record, quota-safe.
 */

import { WORKBENCH_VIEW_KEY } from '../data/workbenchDraft';

/** Pinned positions kept per record. */
export const MAX_PINNED_POSITIONS = 400;

export interface CanvasViewRecord {
    version: string;
    zoom: number;
    pan: { x: number; y: number };
    pinned: Record<string, { x: number; y: number }>;
    savedAt: number;
}

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const round2 = (n: number) => Math.round(n * 100) / 100;

export function saveCanvasView(record: Omit<CanvasViewRecord, 'savedAt'>): void {
    if (!finite(record.zoom) || !finite(record.pan?.x) || !finite(record.pan?.y)) return;
    const pinned: CanvasViewRecord['pinned'] = {};
    let n = 0;
    for (const [id, pos] of Object.entries(record.pinned || {})) {
        if (n >= MAX_PINNED_POSITIONS) break;
        if (!finite(pos?.x) || !finite(pos?.y)) continue;
        pinned[id] = { x: round2(pos.x), y: round2(pos.y) };
        n++;
    }
    const out: CanvasViewRecord = {
        version: record.version,
        zoom: Math.round(record.zoom * 1000) / 1000,
        pan: { x: round2(record.pan.x), y: round2(record.pan.y) },
        pinned,
        savedAt: Date.now(),
    };
    try {
        sessionStorage.setItem(WORKBENCH_VIEW_KEY, JSON.stringify(out));
    } catch {
        // Quota: the view is a convenience. Drop it rather than evict the graph.
        try { sessionStorage.removeItem(WORKBENCH_VIEW_KEY); } catch { /* noop */ }
    }
}

/** The stored record, or null when absent, malformed, or saved for a different canvas version. */
export function loadCanvasView(version: string): CanvasViewRecord | null {
    try {
        const raw = sessionStorage.getItem(WORKBENCH_VIEW_KEY);
        if (!raw) return null;
        const rec = JSON.parse(raw) as CanvasViewRecord;
        if (!rec || rec.version !== version) return null;
        if (!finite(rec.zoom) || rec.zoom <= 0 || !finite(rec.pan?.x) || !finite(rec.pan?.y)) return null;
        return { ...rec, pinned: rec.pinned && typeof rec.pinned === 'object' ? rec.pinned : {} };
    } catch {
        return null;
    }
}

export function clearCanvasView(): void {
    try { sessionStorage.removeItem(WORKBENCH_VIEW_KEY); } catch { /* noop */ }
}

/** The slice of the Cytoscape core this module needs; a fake can stand in for it in tests. */
export interface ViewNode {
    id(): string;
    position(): { x: number; y: number };
    position(p: { x: number; y: number }): unknown;
    addClass(c: string): unknown;
    unlock(): unknown;
}
export interface ViewCy {
    zoom(): number;
    pan(): { x: number; y: number };
    viewport(v: { zoom: number; pan: { x: number; y: number } }): unknown;
    nodes(selector?: string): { length: number; forEach(cb: (n: ViewNode) => void): unknown };
    getElementById(id: string): ViewNode & { length: number };
    on(events: string, handler: (...a: any[]) => void): unknown;
    off(events: string, handler: (...a: any[]) => void): unknown;
}

export interface AttachOptions {
    getVersion: () => string;
    /** Try to put the stored view back (first attach after a return). */
    restore: boolean;
    /** Register an id as pinned in the canvas so later layouts keep it in place. */
    pin?: (id: string) => void;
    onRestored?: (info: { zoom: number; pinned: number }) => void;
    /** Layout is "settled" after this long without a `layoutstop`. */
    quietMs?: number;
    /** Give up waiting for a layout after this long and apply anyway. */
    maxWaitMs?: number;
    saveDebounceMs?: number;
}

/**
 * Wire camera and pinned-position memory onto a live Cytoscape instance.
 * Saving is held back while a restore is pending (the layout fit would
 * overwrite the stored camera). Returns a disposer that flushes one last save.
 */
export function attachCanvasViewMemory(cy: ViewCy, opts: AttachOptions): () => void {
    const quietMs = opts.quietMs ?? 800;
    const maxWaitMs = opts.maxWaitMs ?? 6000;
    const saveDebounceMs = opts.saveDebounceMs ?? 500;

    let restoring = opts.restore && loadCanvasView(opts.getVersion()) !== null;
    let quietTimer: ReturnType<typeof setTimeout> | null = null;
    let maxTimer: ReturnType<typeof setTimeout> | null = null;
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const snapshot = () => {
        if (cy.nodes().length === 0) return;
        const pinned: CanvasViewRecord['pinned'] = {};
        cy.nodes('.manually-positioned').forEach((n) => { pinned[n.id()] = n.position(); });
        saveCanvasView({ version: opts.getVersion(), zoom: cy.zoom(), pan: cy.pan(), pinned });
    };
    const scheduleSave = () => {
        if (restoring || disposed) return;
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => { saveTimer = null; snapshot(); }, saveDebounceMs);
    };

    const finishRestore = (apply: boolean) => {
        if (!restoring) return;
        restoring = false;
        if (quietTimer) { clearTimeout(quietTimer); quietTimer = null; }
        if (maxTimer) { clearTimeout(maxTimer); maxTimer = null; }
        const rec = apply && cy.nodes().length > 0 ? loadCanvasView(opts.getVersion()) : null;
        if (rec) {
            let pinned = 0;
            for (const [id, pos] of Object.entries(rec.pinned)) {
                const node = cy.getElementById(id);
                if (!node || node.length === 0) continue;
                node.position(pos);
                node.addClass('manually-positioned');
                opts.pin?.(id);
                // Pinned for layouts, but still draggable.
                node.unlock();
                pinned++;
            }
            cy.viewport({ zoom: rec.zoom, pan: rec.pan });
            opts.onRestored?.({ zoom: rec.zoom, pinned });
        }
        scheduleSave();
    };

    const armQuiet = () => {
        if (!restoring) return;
        if (quietTimer) clearTimeout(quietTimer);
        quietTimer = setTimeout(() => finishRestore(true), quietMs);
    };
    const onLayoutStop = () => armQuiet();
    // The officer touching the canvas first wins: do not move things under their hand.
    const onUserInput = () => finishRestore(false);
    const onViewChange = () => scheduleSave();

    cy.on('layoutstop', onLayoutStop);
    cy.on('tapstart dragfree', onUserInput);
    cy.on('viewport dragfree', onViewChange);
    if (restoring) {
        armQuiet();
        maxTimer = setTimeout(() => finishRestore(true), maxWaitMs);
    }

    return () => {
        if (disposed) return;
        const wasRestoring = restoring;
        disposed = true;
        cy.off('layoutstop', onLayoutStop);
        cy.off('tapstart dragfree', onUserInput);
        cy.off('viewport dragfree', onViewChange);
        if (quietTimer) clearTimeout(quietTimer);
        if (maxTimer) clearTimeout(maxTimer);
        if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
        // Leaving before the restore ran must not overwrite the stored view with a half-laid-out one.
        if (!wasRestoring) { try { snapshot(); } catch { /* cy may already be destroyed */ } }
    };
}
